import React, {useRef} from 'react';
import styled from 'styled-components';
import {COLORS} from '../styleVars';
import {ImageEntity} from './entitySpec';
import {generateSources, Ratio, ImageSrc} from './ImageUtils';

interface WrapperProps {
  height?: number;
}

const Wrapper = styled.div<WrapperProps>`
  height: 100%;
  display: flex;
  justify-content: inherit;
  align-items: inherit;
  align-content: inherit;
  flex-direction: inherit;
`;

interface ContainerProps {
  height?: number;
}

const Container = styled.div<ContainerProps>`
  //Avoid resize for fullwidth
  height: ${(props) => props.height} ${(props) => (props.height ? 'px' : 'inherit')};
  width: 100%;
`;

interface PictureProps {
  fullscreen: boolean;
}

const Picture = styled.picture<PictureProps>`
  display: flex;
  height: inherit;
  width: inherit;
  ${(props) => (props.fullscreen ? 'position: absolute;' + 'top: 0;' + 'right: 0;' + 'left: 0;' + 'bottom: 0;' : '')}
`;

interface ImgProps {
  entity: ImageEntity;
  radius: string;
  fit: boolean;
  fullscreen?: boolean;
}

const Img = styled.img<ImgProps>`
  display: flex;
  object-fit: ${(props) => (props.fit ? 'contain' : 'cover')};
  border-radius: ${(props) => props.radius};
  object-position: ${(props) =>
    props.entity === 'player' && !props.fullscreen ? 'top' : props.entity === 'branding' ? 'right' : 'center'};
`;

interface PlaceholderProps {
  dimX?: number;
  dimY?: number;
  fit: boolean;
  radius: string;
  unit: string;
}

const Placeholder = styled.div<PlaceholderProps>`
  width: ${(props) => (props.unit === '%' ? '100%' : props.dimX + props.unit)};
  height: ${(props) => props.dimY} ${(props) => props.unit};
  object-fit: ${(props) => (props.fit ? 'contain' : 'cover')};
  border-radius: ${(props) => props.radius};
  background-color: ${COLORS.lightestGrey};
`;

interface ImageProps {
  /**
   * Defines an entity which applies an entity-spec on the image
   */
  entity: ImageEntity;
  /**
   * Passes source and meta-information of the image
   */
  src?: ImageSrc;
  /**
   * Disable lazy-loading for this image
   */
  eager?: boolean;
  /**
   * Defines the height of the image in dp
   */
  height: number;
  /**
   * Defines the width of the image in dp
   */
  width: number;
  /**
   * Defines if image should be shown as rectangle or circle
   */
  circle?: boolean;
  /**
   * Sets the image to fit into the passed dimensions without being cut off
   */
  fit?: boolean;
  /**
   * Increases the max width of an image set by the content on default to display a single image as fullscreen image
   */
  fullscreen?: boolean;
  /**
   * Defines the factor for the ratio of the screen width (for mobile) or the max content width (for destkop) to the actual image width
   * e.g. Screen width of 360dp and image to show with width of 144dp: 360dp/144dp = 2.5
   */
  actualWidthFactor?: number;
  /**
   * Defines the aspect ratio of the image to calc height in dp based on actualWidthFactor
   */
  ratio?: Ratio;
  /**
   * Defines the alt attribute for the image tag
   */
  alt?: string;
  /**
   * Defines the title attribute for the image tag
   */
  title?: string;
  /**
   * Defines the sizes attribute of the source tag
   */
  sizesHint?: string;
}

const Image: React.FC<ImageProps> = ({
  entity,
  eager = false,
  src,
  width,
  height,
  circle = false,
  fit = false,
  fullscreen = false,
  actualWidthFactor,
  ratio,
  sizesHint,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);

  if (src && src.path) {
    const {sources, fallbackSrc} = generateSources(src, entity, ratio, actualWidthFactor, width, sizesHint);

    const widthImg = actualWidthFactor ? '100%' : width;
    const heightImg = actualWidthFactor ? '100%' : height;
    const fallback = (
      <Img
        loading={eager ? 'eager' : 'lazy'}
        entity={entity}
        src={fallbackSrc}
        alt={props?.alt || src.description || ''}
        title={props?.title || src.description || ''}
        width={widthImg}
        height={heightImg}
        fit={fit}
        radius={circle ? '50%' : '0'}
        fullscreen={fullscreen}
      />
    );

    // Generate <picture>-Tag or if not delivered by CDN generate <img>-Tag
    const picture = !src.svg ? (
      <Picture fullscreen={fullscreen}>
        {sources}
        {fallback}
      </Picture>
    ) : (
      fallback
    );

    return (
      <Wrapper height={height}>
        <Container height={height} ref={ref}>
          {picture}
        </Container>
      </Wrapper>
    );
  } else {
    return (
      <Wrapper height={height}>
        <Placeholder
          dimX={width}
          dimY={height || 100}
          fit={fit}
          radius={circle ? '50%' : 'none'}
          unit={height ? 'px' : '%'}
          ref={ref}
        />
      </Wrapper>
    );
  }
};

export {Image};
export type {ImageProps, ImageSrc};
